import { jwtDecode } from 'jwt-decode';
import type { FetchArgs } from '@reduxjs/toolkit/query/react';
import { isString } from 'es-toolkit/predicate';

import type { JwtPayload, AuthTokenStore } from './types';

/**
 * Type guard to check if state has authToken slice
 */
export const isAuthTokenStore = (state: unknown): state is AuthTokenStore => {
  if (typeof state !== 'object' || state === null) {
    return false;
  }
  if (!('authToken' in state)) {
    return false;
  }
  return typeof state.authToken === 'object' && state.authToken !== null;
};

/**
 * Paths sent without a token refresh, each together with everything below it
 * (`/api/v1/password` covers `/session` and `/verify-*`, but not `/passwords`).
 * The password reset flow is anonymous and authorized by its own httpOnly
 * cookie, so a token refresh before or after it would only fail and keep the
 * request from being sent.
 */
const noReauthPaths = [
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/mfa/authenticate',
  '/api/v1/auth/logout',
  '/api/v1/password',
];

const decodeToken = (token?: string): JwtPayload | null => {
  if (!isString(token) || !token) {
    return null;
  }

  try {
    return jwtDecode<JwtPayload>(token);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

export const getTokenPayload = (token?: string) => {
  const decodedPayload = decodeToken(token);

  const defaultPayload: JwtPayload = {
    sub: '',
    iss: '',
    isMfaAuthenticated: false,
    isMfaEnabled: false,
    isEmailConfirmed: false,
    iat: '',
    exp: '',
    tenant: undefined,
  };

  if (!decodedPayload) {
    return { ...defaultPayload, isTokenExpired: true };
  }

  const currentTime = Date.now() / 1000;
  const isTokenExpired = !decodedPayload.exp || +decodedPayload.exp < currentTime;

  return {
    ...decodedPayload,
    isTokenExpired,
  };
};

export function shouldSkipReauth(args: string | FetchArgs) {
  const path = isString(args) ? args : args.url;
  return noReauthPaths.some((skipped) => path === skipped || path.startsWith(`${skipped}/`));
}

/**
 * Type guard to check if object has a specific property
 */
export const hasProperty = <T extends object, K extends PropertyKey>(
  obj: T,
  key: K,
): obj is T & Record<K, unknown> => {
  return Object.prototype.hasOwnProperty.call(obj, key);
};
